import React, { useEffect, useState } from "react";
import { Table, Button, Modal, Form, Input, Select, Space, message, Popconfirm, Tag } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import PageHeader from "../../components/ui/PageHeader";
import PageTransition from "../../components/ui/PageTransition";
import EmptyState from "../../components/ui/EmptyState";

const scopeOptions = [
  { value: "all", label: "All Employees" },
  { value: "role", label: "Role" },
  { value: "department", label: "Department" },
  { value: "employee", label: "Employee" },
  { value: "employmentType", label: "Employment Type" },
];

const employmentTypeOptions = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const Benefits = () => {
  const { request } = useRequest();
  const [benefits, setBenefits] = useState([]);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [scope, setScope] = useState("all");
  const [form] = Form.useForm();

  const fetchBenefits = () => {
    request({ url: apiPath.benefits, method: "GET", onSuccess: (res) => setBenefits(res?.data?.data || []) });
  };

  const fetchLookups = () => {
    request({ url: apiPath.roles, method: "GET", onSuccess: (res) => setRoles(res?.data?.data || []) });
    request({ url: apiPath.departments, method: "GET", onSuccess: (res) => setDepartments(res?.data?.data || []) });
    request({ url: apiPath.employees, method: "GET", onSuccess: (res) => setEmployees(res?.data?.employees || []) });
  };

  useEffect(() => {
    fetchBenefits();
    fetchLookups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditing(null);
    setScope("all");
    form.resetFields();
    form.setFieldsValue({ scope: "all" });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    setScope(record.scope);
    form.setFieldsValue({ ...record, scopeRefId: record.scopeRefId || undefined });
    setModalOpen(true);
  };

  const scopeLabel = (record) => {
    if (record.scope === "all") return "All Employees";
    if (record.scope === "role") return roles.find((r) => r._id === record.scopeRefId)?.name || record.scopeRefId;
    if (record.scope === "department") return departments.find((d) => d._id === record.scopeRefId)?.name || record.scopeRefId;
    if (record.scope === "employee") return employees.find((e) => e._id === record.scopeRefId)?.name || record.scopeRefId;
    if (record.scope === "employmentType") return record.scopeRefId;
    return "";
  };

  const scopeRefOptions = () => {
    if (scope === "role") return roles.map((r) => ({ value: r._id, label: r.name }));
    if (scope === "department") return departments.map((d) => ({ value: d._id, label: d.name }));
    if (scope === "employee") return employees.map((e) => ({ value: e._id, label: e.name }));
    if (scope === "employmentType") return employmentTypeOptions;
    return [];
  };

  const onSubmit = () => {
    form.validateFields().then((values) => {
      const payload = { ...values };
      if (payload.scope === "all") payload.scopeRefId = null;
      const url = editing ? `${apiPath.benefits}/${editing._id}` : apiPath.benefits;
      const method = editing ? "PUT" : "POST";
      request({
        url,
        method,
        data: payload,
        onSuccess: () => {
          message.success(editing ? "Benefit updated" : "Benefit created");
          setModalOpen(false);
          fetchBenefits();
        },
      });
    });
  };

  const onDelete = (record) => {
    request({
      url: `${apiPath.benefits}/${record._id}`,
      method: "DELETE",
      onSuccess: () => {
        message.success("Benefit deleted");
        fetchBenefits();
      },
    });
  };

  const columns = [
    { title: "Name", dataIndex: "name" },
    { title: "Description", dataIndex: "description", ellipsis: true },
    { title: "Scope", dataIndex: "scope", render: (s) => <Tag>{scopeOptions.find((o) => o.value === s)?.label || s}</Tag> },
    { title: "Applies To", render: (_, record) => scopeLabel(record) },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => openEdit(record)} />
          <Popconfirm title="Delete this benefit?" onConfirm={() => onDelete(record)}>
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <PageTransition>
      <PageHeader
        title="Benefits"
        subtitle="Configure employee benefits and who they apply to"
        actions={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add Benefit
          </Button>
        }
      />
      <Table
        rowKey="_id"
        columns={columns}
        dataSource={benefits}
        pagination={{ pageSize: 10 }}
        locale={{ emptyText: <EmptyState message="No benefits configured yet" actionLabel="Add Benefit" onAction={openCreate} /> }}
      />

      <Modal title={editing ? "Edit Benefit" : "Add Benefit"} open={modalOpen} onCancel={() => setModalOpen(false)} onOk={onSubmit} destroyOnClose>
        <Form layout="vertical" form={form}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="scope" label="Scope" rules={[{ required: true }]}>
            <Select
              options={scopeOptions}
              onChange={(v) => {
                setScope(v);
                form.setFieldsValue({ scopeRefId: undefined });
              }}
            />
          </Form.Item>
          {scope !== "all" && (
            <Form.Item name="scopeRefId" label="Applies To" rules={[{ required: true }]}>
              <Select options={scopeRefOptions()} showSearch optionFilterProp="label" />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </PageTransition>
  );
};

export default Benefits;
